import React from 'react';
import { Trash2, ShieldAlert, Mail, Activity, RefreshCcw, DatabaseZap } from 'lucide-react';
import LegalLayout from '../../components/LegalLayout';

const DataDeletionPage: React.FC = () => {
  const lastUpdate = "24 de Março de 2026";

  return (
    <LegalLayout
      title="Exclusão de Dados"
      description="Transparência total sobre como solicitar a remoção definitiva de suas informações e dados operacionais da nossa plataforma."
    >
      <div className="space-y-12 text-secondary/90 leading-relaxed">

        {/* Intro */}
        <section className="bg-red-500/5 border border-red-500/10 rounded-3xl p-8 mb-8">
          <div className="flex items-center gap-4 mb-4 text-red-400">
            <Trash2 className="w-8 h-8" />
            <h2 className="text-2xl font-black text-white">Direito ao Esquecimento</h2>
          </div>
          <p>
            No <strong>Gastroflow</strong>, respeitamos seu direito à privacidade e ao controle total sobre seus dados. Conforme exigido pela <strong>LGPD</strong>, esta página detalha os procedimentos para a exclusão definitiva de registros associados à sua conta ou ao seu estabelecimento.
          </p>
          <p className="mt-4 text-sm text-secondary/50 font-medium italic">
            Última atualização: {lastUpdate}
          </p>
        </section>

        {/* 1. Como solicitar */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <Mail className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">1. Como solicitar a exclusão?</h2>
          </div>
          <p>Existem dois caminhos para solicitar a remoção de dados:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Autoatendimento:</strong> No painel administrativo em <em>Configurações {'>'} Segurança {'>'} Excluir Conta</em>.</li>
            <li><strong>Via Suporte:</strong> Enviando um e-mail para <a href="mailto:exclusao@garcomdigital.site" className="text-primary font-bold hover:underline">exclusao@garcomdigital.site</a> utilizando o e-mail de administrador cadastrado na plataforma.</li>
          </ul>
        </section>

        {/* 2. O que é excluído */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <DatabaseZap className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">2. O que será removido?</h2>
          </div>
          <p>Ao processar uma solicitação de exclusão total, o Gastroflow remove permanentemente:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Dados de Registro:</strong> Nomes, e-mails, senhas (hashes) e documentos vinculados.</li>
            <li><strong>Histórico Operacional:</strong> Registros de pedidos, reservas e logs de atividades.</li>
            <li><strong>Tokens de Integração:</strong> Conexões com Mercado Pago, WhatsApp e outros provedores são revogadas e deletadas.</li>
            <li><strong>Mídia:</strong> Logos, fotos de cardápio e modelos 3D armazenados em nossos servidores.</li>
          </ul>
        </section>

        {/* 3. Retenção Legal */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <ShieldAlert className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">3. Limitações e Retenção Legal</h2>
          </div>
          <p>
            Algumas informações **não podem ser excluídas imediatamente** por força de lei ou por razões de segurança crítica:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Obrigações Fiscais:</strong> Registros de faturas e transações financeiras (Notas Fiscais) são mantidos por 5 anos, conforme legislação brasileira.</li>
            <li><strong>Segurança:</strong> Logs de auditoria para prevenção de fraudes podem ser mantidos sob anonimização por tempo determinado.</li>
            <li><strong>Processamento Pendente:</strong> Se houver transações financeiras em fase de liquidação, a exclusão ocorrerá somente após a conclusão do repasse.</li>
          </ul>
        </section>

        {/* 4. Integrações Externas */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <RefreshCcw className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">4. Desconexão de Terceiros</h2>
          </div>
          <p>
            Importante: A exclusão no Gastroflow remove a conexão com o WhatsApp Business ou Mercado Pago, mas <strong>não exclui seus dados diretamente nestes provedores</strong>. Recomendamos que você também revise as configurações de privacidade nesses serviços externos.
          </p>
        </section>

        {/* 5. Prazo */}
        <section className="space-y-4 p-6 bg-white/5 rounded-2xl border border-white/10">
          <div className="flex items-center gap-3 text-white">
            <Activity className="w-6 h-6 text-primary" />
            <h2 className="text-lg font-bold">Prazo de Resposta</h2>
          </div>
          <p className="text-sm">Solicitações manuais via e-mail são processadas em até <strong>15 dias úteis</strong>. Solicitações via painel administrativo têm processamento imediato para a maioria dos dados voláteis.</p>
        </section>

        <section className="pt-8 border-t border-white/10 text-center text-sm text-secondary/60">
          <p>
            Dúvidas sobre o tratamento de seus dados? <br />
            <a href="mailto:dpo@garcomdigital.site" className="text-primary font-bold hover:underline">dpo@garcomdigital.site</a>
          </p>
        </section>
      </div>
    </LegalLayout>
  );
};

export default DataDeletionPage;
